/* eslint-disable jsx-a11y/anchor-is-valid */
import React, { useState, useEffect } from 'react';
import { matchPath } from 'react-router-dom';
import dispatchEvent from 'utils/dispatch_event';
import PropTypes from 'prop-types';
import cn from 'classnames';
import { AlertCircle } from 'react-feather';
import qs from 'qs';
import { Text, TEXT_VARIANTS } from 'ui/Typography';
import Breadcrumbs, { Crumb } from 'components/ui/Breadcrumbs';
import AssignModal from 'components/AssignModal';
import useFeatureFlags from 'hooks/useFeatureFlags';
import usePermissions from 'hooks/usePermissions';
import { routes, createQuery } from 'constants/config';
import LessonBackToPath from './BackToPath';
import LessonTitle from './LessonTitle';
import SectionTitle from './SectionTitle';
import LessonHeaderAskTheExpert from './LessonHeaderAskTheExpert';
import EditLessonButton from './EditLessonButton';
import LessonHeaderSectionList from './LessonHeaderSectionList';

import LessonHeaderSectionButton from './LessonHeaderSectionButton';
import ToggleLessonNav from './ToggleLessonNav';

const isLibraryPath = pathname => !!matchPath(pathname, { path: '/library' });

const LessonHeader = ({
  title,
  section,
  sections,
  pathId,
  pathName,
  isPreviewing,
  currentSectionIndex,
  totalSectionCount,
  unansweredRequiredQuestionsCount = 0,
  isEmbed,
  lessonUrl,
  hasLessonExpert,
  hasEmail,
  hasCurrentUser,
  currentLearningPath,
  askTheExpertPostUrl,
  expertIconName,
  expertName,
  metadata,
}) => {
  const isPathLesson = !!currentLearningPath;
  const [isHovered, setIsHovered] = useState(false);
  const [isSectionListOpen, setIsSectionListOpen] = useState(false);
  const [sectionListOpenedByKeyboard, setSectionListOpenedByKeyboard] =
    useState(false);

  const isScormSection = section?.isScormSection;
  const [isOpen, setIsOpen] = useState(!isScormSection);

  const onOpenSectionlist = () => {
    setIsSectionListOpen(true);
    setSectionListOpenedByKeyboard(false);
  };

  const onCloseSectionList = () => {
    setIsSectionListOpen(false);
    setSectionListOpenedByKeyboard(false);
  };

  const toggleOpen = shouldHide => {
    setIsOpen(shouldHide);
    setTimeout(() => dispatchEvent('resize'), 250);
  };

  useEffect(() => {
    if (isScormSection) {
      pubsub.publish('showNonUnifiedScormToggle', true);
      pubsub.subscribe('hideLessonHeader', toggleOpen);
    }

    return () => {
      if (isScormSection) {
        pubsub.unsubscribe('hideLessonHeader', shouldHide => {
          setIsOpen(shouldHide);
        });
      }
    };
  }, [isOpen, isScormSection]);

  const assignModalState = {
    meta: {
      features: {
        resource_experiment: useFeatureFlags('resource_experiment'),
      },
      i18n: {
        smart_groups_calculation_tooltip: t(
          'assign_modal.smart_groups_calculation_tooltip',
        ),
        invalid_group_tooltip: t('assign_modal.invalid_group_tooltip_admin'),
        button_text: t('actions.assign'),
        assign_error_message: t('assign_modal.assign_error_message'),
      },
    },
  };

  const hasUnificationFlag = useFeatureFlags(['readiness-enable-unification']);
  const { isPermitted: canCreateAssignments } = usePermissions({
    type: 'assignment',
    action: 'create',
  });

  const urlQuery = () => {
    const locationQuery = qs.parse(window.location.search, {
      ignoreQueryPrefix: true,
    });
    const query = {};
    if (locationQuery.embed === 'true') {
      query.embed = true;
    }
    return query;
  };

  const pathRoute = () => {
    const query = urlQuery();
    if (isLibraryPath(window.location.pathname)) {
      return routes.LIBRARY_PATH(pathId, query);
    }
    return isPreviewing
      ? routes.PREVIEW_LEARNER_PATH(pathId, query)
      : routes.LEARNER_PATH(pathId, query);
  };

  const titleBreadCrumb =
    isScormSection && section.title ? `${title}: ${section.title}` : title;

  return (
    <header
      className="LessonHeader"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {hasUnificationFlag && (
        <div className="LessonHeaderBreadcrumbs d-f fd-r ai-c jc-sb">
          <Breadcrumbs
            parentUrl={`${routes.LEARN}${createQuery(urlQuery())}`}
            parentTitle={t('learn.sidebar.headers.learn')}
            className="mr-24 mt-1 mb-1"
            hideNav
          >
            {pathName && <Crumb href={pathRoute()}>{pathName}</Crumb>}
            <Crumb>{titleBreadCrumb}</Crumb>
          </Breadcrumbs>
          <div className="d-fi ai-c">
            {isScormSection && (
              <ToggleLessonNav
                isOpen={isOpen}
                setIsOpen={toggleOpen}
                showPopover={metadata.canSeeLessonDetailsHiddenPopover}
              />
            )}

            {canCreateAssignments && (
              <div className="fs-0 u-hideBelowSM">
                <AssignModal state={assignModalState} />
              </div>
            )}
          </div>
        </div>
      )}
      <div
        className={cn('LessonHeaderNav', {
          collapsed: !isOpen,
          hasSectionTitle: section?.title,
        })}
      >
        <div className="LessonHeader-layout">
          {isPathLesson && !hasUnificationFlag && (
            <div className="d-f jc-sb">
              <LessonBackToPath
                pathId={pathId}
                pathName={pathName}
                isPreviewing={isPreviewing}
              />
              <LessonHeaderAskTheExpert
                hasLessonExpert={hasLessonExpert}
                hasEmail={hasEmail}
                isPreviewing={isPreviewing}
                askTheExpertPostUrl={askTheExpertPostUrl}
                sectionId={section?.id}
                expertIconName={expertIconName}
                expertName={expertIconName}
                lessonTitle={title}
              />
            </div>
          )}
          {hasUnificationFlag && isPathLesson && (
            <LessonHeaderAskTheExpert
              hasLessonExpert={hasLessonExpert}
              hasEmail={hasEmail}
              isPreviewing={isPreviewing}
              askTheExpertPostUrl={askTheExpertPostUrl}
              sectionId={section?.id}
              expertIconName={expertIconName}
              expertName={expertIconName}
              lessonTitle={title}
            />
          )}
          <div className="d-f jc-sb">
            <div className="wb-bw ml-8">
              <LessonTitle
                title={title}
                isEmbed={isEmbed}
                externalLink={lessonUrl}
              />
              <SectionTitle title={section?.title} />
            </div>
            <div className="d-f">
              {!isPathLesson && (
                <LessonHeaderAskTheExpert
                  hasLessonExpert={hasLessonExpert}
                  hasEmail={hasEmail}
                  isPreviewing={isPreviewing}
                  askTheExpertPostUrl={askTheExpertPostUrl}
                  sectionId={section?.id}
                  expertIconName={expertIconName}
                  expertName={expertIconName}
                  lessonTitle={title}
                />
              )}
              {metadata?.permissions?.canEditLesson && (
                <EditLessonButton editLessonSectionUrl={section?.editUrl} />
              )}
            </div>
          </div>

          <div className="d-f">
            <LessonHeaderSectionButton
              className="mr-8"
              currentSectionIndex={section?.index}
              totalSectionCount={totalSectionCount}
              hovered={isHovered}
              onMouseDown={e => {
                // Prevents mouse click focus ring
                e.preventDefault();
              }}
              onClick={() => !isSectionListOpen && onOpenSectionlist()}
              onKeyUp={e => {
                if (e.keyCode === 13) {
                  setSectionListOpenedByKeyboard(true);
                }
              }}
            />
            {unansweredRequiredQuestionsCount > 0 && (
              <Text className="d-f ai-c" variant={TEXT_VARIANTS.INSTRUX_ERROR}>
                <AlertCircle size="1.25rem" className="mr-8" />{' '}
                {t('learning.lessons.required_questions', {
                  count: unansweredRequiredQuestionsCount,
                })}
              </Text>
            )}
          </div>
        </div>
        <LessonHeaderSectionList
          isOpen={isSectionListOpen}
          focusOnOpen={sectionListOpenedByKeyboard}
          onClose={() => isSectionListOpen && onCloseSectionList()}
          section={section}
          sections={sections}
          isPreviewing={isPreviewing}
          hasCurrentUser={hasCurrentUser}
        />
      </div>
    </header>
  );
};

LessonHeader.propTypes = {
  title: PropTypes.string,
  pathId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  pathName: PropTypes.string,
  totalSectionCount: PropTypes.number,
  unansweredRequiredQuestionsCount: PropTypes.number,
  isEmbed: PropTypes.bool,
  hasLessonExpert: PropTypes.bool,
  hasEmail: PropTypes.bool,
  hasCurrentUser: PropTypes.bool,
  isPreviewing: PropTypes.bool,
  lessonUrl: PropTypes.string,
  askTheExpertPostUrl: PropTypes.string,
  section: PropTypes.shape({
    title: PropTypes.string,
    index: PropTypes.number,
    isScormSection: PropTypes.bool,
    editUrl: PropTypes.string,
    id: PropTypes.number,
  }),
  expertIconName: PropTypes.string,
  expertName: PropTypes.string,
  metadata: PropTypes.shape({
    permissions: PropTypes.shape({
      canEditLesson: PropTypes.bool,
    }),
  }),
};

export default props => <LessonHeader {...props} />;
